import { IDataSource } from "client/Data/common";
import { IPageable } from "client/Data/pagination";
import { DataSourceSortableState } from "client/Data/sorting";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { calculatePaginationInfo } from "./createLocalDataSource";
import { useDeepComparison } from "./useDeepComparison";
import { useForceRerender } from "./useForceRerender";
import { useValueEqualMemo } from "./useValueEqualMemo";

export type RemoteDataSourceState = "loading" | "error" | "ready";

export enum LazyLoadCursorKind {
	None = 0,
	AfterLastItem = 1,
	SkipTake = 2,
	SkipToken = 3
}

export function createRemoteDataSource<
	TDataItem,
	TFilterState = undefined,
	TGlobalContext = void
>(): RemoteDataSourceConfigurator<TDataItem, TFilterState, TGlobalContext> {
	const result = new RemoteDataSourceConfiguratorImplementation<
		TDataItem,
		TFilterState,
		TGlobalContext
	>();

	return result;
}

type RemoteDataSourceConfigurator<TDataItem, TFilterState, TGlobalContext> = {
	setConfiguration<TConfiguration extends RemoteDataSourceConfiguration>(
		configuration: TConfiguration
	): ResultPartRemoteDataSourceBuilder<TDataItem, TFilterState, TConfiguration, TGlobalContext>;
};

type RemoteDataSourceConfiguration =
	& FilteringPartRemoteDataSourceConfiguration
	& SearchPartRemoteDataSourceConfiguration
	& PaginationPartRemoteDataSourceConfiguration
	& LazyLoadPartRemoteDataSourceConfiguration;

type HasFilteringPartRemoteDataSourceConfiguration = { hasFiltering: true; };
type FilteringPartRemoteDataSourceConfiguration =
	| HasFilteringPartRemoteDataSourceConfiguration
	| { hasFiltering?: false; };

type HasSearchPartRemoteDataSourceConfiguration = { hasSearch: true; };
type SearchPartRemoteDataSourceConfiguration =
	| HasSearchPartRemoteDataSourceConfiguration
	| { hasSearch?: false; };

type HasPaginationPartRemoteDataSourceConfiguration = { hasPagination: true; };
type PaginationPartRemoteDataSourceConfiguration =
	| HasPaginationPartRemoteDataSourceConfiguration
	| { hasPagination?: false; };

type AfterLastItemLazyLoadPartRemoteDataSourceConfiguration = { lazyLoadCursor: LazyLoadCursorKind.AfterLastItem; };
type SkipTakeLazyLoadPartRemoteDataSourceConfiguration = { lazyLoadCursor: LazyLoadCursorKind.SkipTake; };
type SkipTokenLazyLoadPartRemoteDataSourceConfiguration = { lazyLoadCursor: LazyLoadCursorKind.SkipToken; };

type HasLazyLoadPartRemoteDataSourceConfiguration =
	| AfterLastItemLazyLoadPartRemoteDataSourceConfiguration
	| SkipTakeLazyLoadPartRemoteDataSourceConfiguration
	| SkipTokenLazyLoadPartRemoteDataSourceConfiguration;

type LazyLoadPartRemoteDataSourceConfiguration =
	| HasLazyLoadPartRemoteDataSourceConfiguration
	| { lazyLoadCursor?: LazyLoadCursorKind.None; };

type GetHookOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext> = {
	performRequest: (
		options: RemoteDataSourceRequestOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
	) => Promise<RemoteDataSourceResponse<TDataItem, TConfiguration>>;
};

type ResultPartRemoteDataSourceBuilder<TDataItem, TFilterState, TConfiguration, TGlobalContext> = {
	getHook(
		options: GetHookOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
	): UseRemoteDataSource<TDataItem, TFilterState, TConfiguration, TGlobalContext>;
};

type UseRemoteDataSource<TDataItem, TFilterState, TConfiguration, TGlobalContext> = (
	options: UseRemoteDataSourceOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
) => RemoteDataSource<TDataItem, TConfiguration>;

type UseRemoteDataSourceOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext> =
	& SortingPartUseRemoteDataSourceOptions<TDataItem>
	& FilteringPartUseRemoteDataSourceOptions<TConfiguration, TFilterState>
	& SearchPartUseRemoteDataSourceOptions<TConfiguration>
	& PaginationPartUseRemoteDataSourceOptions<TConfiguration>
	& LazyLoadPartUseRemoteDataSourceOptions<TConfiguration>
	& ErrorHandlingPartUseRemoteDataSourceOptions
	& GlobalContextPartUseRemoteDataSourceOptions<TGlobalContext>;

type SortingPartUseRemoteDataSourceOptions<TDataItem> = {
	sorting?: DataSourceSortableState<TDataItem>;
};

type HasFilteringPartUseRemoteDataSourceOptions<TFilterState> = { filtering: TFilterState; };
type FilteringPartUseRemoteDataSourceOptions<TConfiguration, TFilterState> =
	TConfiguration extends HasFilteringPartRemoteDataSourceConfiguration
		? HasFilteringPartUseRemoteDataSourceOptions<TFilterState>
		: {};

type HasSearchPartUseRemoteDataSourceOptions = { searchTerm: string; };
type SearchPartUseRemoteDataSourceOptions<TConfiguration> =
	TConfiguration extends HasSearchPartRemoteDataSourceConfiguration
		? HasSearchPartUseRemoteDataSourceOptions
		: {};

type PaginationOptions = {
	currentPageNumber: number;
	amountPerPage?: number;
};
const DEFAULT_AMOUNT_PER_PAGE = 25;

type HasPaginationPartUseRemoteDataSourceOptions = { pagination: PaginationOptions; };
type PaginationPartUseRemoteDataSourceOptions<TConfiguration> =
	TConfiguration extends HasPaginationPartRemoteDataSourceConfiguration
		? HasPaginationPartUseRemoteDataSourceOptions
		: {};

type HasLazyLoadPartUseRemoteDataSourceOptions = { desiredAmountOfItems: number; };
type LazyLoadPartUseRemoteDataSourceOptions<TConfiguration> =
	TConfiguration extends HasLazyLoadPartRemoteDataSourceConfiguration
		? HasLazyLoadPartUseRemoteDataSourceOptions
		: {};

type ErrorHandlingPartUseRemoteDataSourceOptions = {
	onError?(error: unknown): void;
};

type GlobalContextPartUseRemoteDataSourceOptions<TGlobalContext> =
	TGlobalContext extends void
		? { globalContext?: undefined; }
		: { globalContext: TGlobalContext; };

type BaseRemoteDataSource<TDataItem> = IDataSource<TDataItem> & {
	getLoadingState(): RemoteDataSourceState;
	reload(): void;
};
type PaginationResult<TConfiguration> =
	TConfiguration extends HasPaginationPartRemoteDataSourceConfiguration ? IPageable : { };

type LazyLoadStatusResult = {
	hasLazyLoadError: true;
	retryToLoadLastPage(): void;
} | {
	hasLazyLoadError: false;
	retryToLoadLastPage: undefined;
};

type LazyLoadResult<TConfiguration> =
	TConfiguration extends HasLazyLoadPartRemoteDataSourceConfiguration
		? {
			hasMoreOnServer: boolean;
		} & LazyLoadStatusResult
		:
		{};

type RemoteDataSource<TDataItem, TConfiguration> =
	& BaseRemoteDataSource<TDataItem>
	& PaginationResult<TConfiguration>
	& LazyLoadResult<TConfiguration>;

type RemoteDataSourceRequestOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext> =
	& SortingPartRemoteDataSourceRequestOptions<TDataItem>
	& FilteringPartRemoteDataSourceRequestOptions<TConfiguration, TFilterState>
	& SearchPartRemoteDataSourceRequestOptions<TConfiguration>
	& PaginationPartRemoteDataSourceRequestOptions<TConfiguration>
	& LazyLoadPartRemoteDataSourceRequestOptions<TDataItem, TConfiguration>
	& GlobalContextPartRemoteDataSourceRequestOptions<TGlobalContext>
	& CancellationTokenPartRemoteDataSourceRequestOptions;

type SortingPartRemoteDataSourceRequestOptions<TDataItem> = {
	sorting?: DataSourceSortableState<TDataItem>;
};

type HasFilteringPartRemoteDataSourceRequestOptions<TFilterState> = {
	filterState: TFilterState;
};
type FilteringPartRemoteDataSourceRequestOptions<TConfiguration, TFilterState> =
	TConfiguration extends HasFilteringPartRemoteDataSourceConfiguration
		? HasFilteringPartRemoteDataSourceRequestOptions<TFilterState>
		: {};

type HasSearchPartRemoteDataSourceRequestOptions = {
	searchTerm: string;
};
type SearchPartRemoteDataSourceRequestOptions<TConfiguration> =
	TConfiguration extends HasSearchPartRemoteDataSourceConfiguration
		? HasSearchPartRemoteDataSourceRequestOptions
		: {};

type HasPaginationPartRemoteDataSourceRequestOptions = {
	pageNumber: number;
	amountPerPage: number;
};

type PaginationPartRemoteDataSourceRequestOptions<TConfiguration> =
	TConfiguration extends HasPaginationPartRemoteDataSourceConfiguration
		? HasPaginationPartRemoteDataSourceRequestOptions
		: {};

type AfterLastItemLazyLoadPartRemoteDataSourceRequestOptions<TDataItem> = {
	after: TDataItem | undefined;
	amount: number;
};

type SkipTakeLazyLoadPartRemoteDataSourceRequestOptions = {
	skip: number;
	amount: number;
};

type SkipTokenLazyLoadPartRemoteDataSourceRequestOptions = {
	skipToken: string | null;
	amount: number;
};

type LazyLoadPartRemoteDataSourceRequestOptions<TDataItem, TConfiguration> =
	TConfiguration extends AfterLastItemLazyLoadPartRemoteDataSourceConfiguration
		? AfterLastItemLazyLoadPartRemoteDataSourceRequestOptions<TDataItem> :
	TConfiguration extends SkipTakeLazyLoadPartRemoteDataSourceConfiguration
		? SkipTakeLazyLoadPartRemoteDataSourceRequestOptions:
	TConfiguration extends SkipTokenLazyLoadPartRemoteDataSourceConfiguration
		? SkipTokenLazyLoadPartRemoteDataSourceRequestOptions:
	{};

type GlobalContextPartRemoteDataSourceRequestOptions<TGlobalContext> =
	TGlobalContext extends void
		? { globalContext?: undefined; }
		: { globalContext: TGlobalContext; };

type CancellationTokenPartRemoteDataSourceRequestOptions = {
	signal: AbortSignal;
};

type RemoteDataSourceResponse<TDataItem, TConfiguration> =
	TConfiguration extends HasPaginationPartRemoteDataSourceConfiguration ? PageResult<TDataItem> :
	TConfiguration extends AfterLastItemLazyLoadPartRemoteDataSourceConfiguration ? LazyLoadPageResult<TDataItem> :
	TConfiguration extends SkipTakeLazyLoadPartRemoteDataSourceConfiguration ? LazyLoadPageResult<TDataItem> :
	TConfiguration extends SkipTokenLazyLoadPartRemoteDataSourceConfiguration ? SkipTokenPageResult<TDataItem> :
	readonly TDataItem[];

class RemoteDataSourceConfiguratorImplementation<
	TDataItem,
	TFilterState,
	TGlobalContext
> implements RemoteDataSourceConfigurator<
	TDataItem,
	TFilterState,
	TGlobalContext
> {
	public setConfiguration<TConfiguration extends RemoteDataSourceConfiguration>(
		configuration: TConfiguration
	): ResultPartRemoteDataSourceBuilder<TDataItem, TFilterState, TConfiguration, TGlobalContext> {
		return new RemoteDataSourceBuilderImplementation<
			TDataItem,
			TFilterState,
			TConfiguration,
			TGlobalContext
		>(
			configuration
		);
	}
}

class RemoteDataSourceBuilderImplementation<
	TDataItem,
	TFilterState,
	TConfiguration extends RemoteDataSourceConfiguration,
	TGlobalContext
> implements
	ResultPartRemoteDataSourceBuilder<TDataItem, TFilterState, TConfiguration, TGlobalContext>
{
	private readonly _configuration: TConfiguration;

	constructor(configuration: TConfiguration) {
		this._configuration = configuration;
	}

	public getHook(
		options: GetHookOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
	): UseRemoteDataSource<TDataItem, TFilterState, TConfiguration, TGlobalContext> {
		if (doesSupportLazyLoad(this._configuration)) {
			return getHookForLazyLoad<
				TDataItem,
				TFilterState,
				TConfiguration,
				TGlobalContext
			>(
				this._configuration,
				options
			);
		}

		return getHookForPaginationOrDefault<
			TDataItem,
			TFilterState,
			TConfiguration,
			TGlobalContext
		>(
			this._configuration,
			options
		);
	}
}

function getHookForPaginationOrDefault<
	TDataItem,
	TFilterState,
	TConfiguration extends RemoteDataSourceConfiguration,
	TGlobalContext
>(
	configuration: TConfiguration,
	options: GetHookOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
): UseRemoteDataSource<TDataItem, TFilterState, TConfiguration, TGlobalContext> {

	const {
		performRequest
	} = options;

	return (
		hookOptionsWithoutPreservedRef: UseRemoteDataSourceOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
	): RemoteDataSource<TDataItem, TConfiguration> => {
		const hookOptions: UseRemoteDataSourceOptions<
			TDataItem,
			TFilterState,
			TConfiguration,
			TGlobalContext
			> = useValueEqualMemo(
				() => hookOptionsWithoutPreservedRef,
				[hookOptionsWithoutPreservedRef]
			);
		const [itemsState, setItemsState] = useState<PaginationItemsState<TDataItem>>({
			currentItems: [],
			totalAmountOfItems: 0
		});

		const [getLoadingState, setLoadingState] = useImmediateState<RemoteDataSourceState>("ready");
		const [reload, reloadVersion] = useForceRerender();

		useEffect(
			() => {
				hasImplicitDependencyOn(reloadVersion);

				async function fetchItems(signal: AbortSignal): Promise<void> {
					const {
						globalContext,
						sorting,
						onError
					} = hookOptions;

					const filteringOptions: FilteringPartRemoteDataSourceRequestOptions<TConfiguration, TFilterState> = getFilteringRequestPart<
						TDataItem,
						TFilterState,
						TConfiguration,
						TGlobalContext
					>(
						configuration,
						hookOptions
					);

					const searchOptions: SearchPartRemoteDataSourceRequestOptions<TConfiguration> = getSearchRequestPart<
						TDataItem,
						TFilterState,
						TConfiguration,
						TGlobalContext
					>(
						configuration,
						hookOptions
					);

					const paginationOptions: PaginationPartRemoteDataSourceRequestOptions<TConfiguration> = getPaginationRequestPart<
						TDataItem,
						TFilterState,
						TConfiguration,
						TGlobalContext
					>(
						configuration,
						hookOptions
					);

					const lazyLoadOptions = { } as LazyLoadPartRemoteDataSourceRequestOptions<TDataItem, TConfiguration>;

					const globalContextOptions = {
						globalContext
					} as GlobalContextPartRemoteDataSourceRequestOptions<TGlobalContext>;

					const sortingOptions: SortingPartRemoteDataSourceRequestOptions<TDataItem> = {
						sorting
					};

					const cancellationOptions: CancellationTokenPartRemoteDataSourceRequestOptions = {
						signal
					};

					try {
						setLoadingState("loading");
						const response: RemoteDataSourceResponse<TDataItem, TConfiguration> = await performRequest({
							...filteringOptions,
							...searchOptions,
							...paginationOptions,
							...globalContextOptions,
							...lazyLoadOptions,
							...sortingOptions,
							...cancellationOptions
						});

						if (signal.aborted) {
							return;
						}

						if (doesSupportPagination(configuration)) {
							if (!isPaginationResponse<TDataItem>(response)) {
								throw new Error("Expected pagination response at this point.");
							}

							setItemsState({
								currentItems: response.items,
								totalAmountOfItems: response.totalCount
							});
						} else {
							if (!isArrayResponse<TDataItem>(response)) {
								throw new Error("Expected array of items at this point.");
							}

							setItemsState({
								currentItems: response,
								totalAmountOfItems: response.length
							});
						}

						setLoadingState("ready");
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						setItemsState({
							currentItems: [],
							totalAmountOfItems: 0
						});

						if (onError) {
							onError(e);
						} else {
							console.error("Unable to perform pagination request", e);
						}

						setLoadingState("error");
					}
				}

				const cancellation: AbortController = new AbortController();
				fetchItems(cancellation.signal);
				return () => cancellation.abort();
			},
			[
				hookOptions,
				reloadVersion,
				setLoadingState
			]
		);

		const {
			currentItems,
			totalAmountOfItems
		} = itemsState;

		const result: BaseRemoteDataSource<TDataItem> = {
			getLoadingState,
			currentItems,
			reload
		};

		let paginationResult: PaginationResult<TConfiguration>;

		if (doesSupportPagination(configuration)) {
			if (!arePaginationOptionsProvided(hookOptions)) {
				throw new Error("Pagination options are not provided.");
			}
			const {
				currentPageNumber,
				amountPerPage = DEFAULT_AMOUNT_PER_PAGE
			} = hookOptions.pagination;

			const pageable: IPageable = {
				pagination: calculatePaginationInfo(
					totalAmountOfItems,
					amountPerPage,
					currentPageNumber
				)
			};

			paginationResult = pageable as PaginationResult<TConfiguration>;
		} else {
			paginationResult = { } as PaginationResult<TConfiguration>;
		}

		const lazyLoadResult = { } as LazyLoadResult<TConfiguration>;

		return {
			...result,
			...paginationResult,
			...lazyLoadResult
		};
	};
}

function getHookForLazyLoad<
	TDataItem,
	TFilterState,
	TConfiguration extends RemoteDataSourceConfiguration,
	TGlobalContext
>(
	configuration: TConfiguration & HasLazyLoadPartRemoteDataSourceConfiguration,
	options: GetHookOptions<
		TDataItem,
		TFilterState,
		TConfiguration & HasLazyLoadPartRemoteDataSourceConfiguration,
		TGlobalContext
	>
): UseRemoteDataSource<TDataItem, TFilterState, TConfiguration, TGlobalContext> {
	const {
		performRequest
	} = options;
	return (
		hookOptionsWithoutPreservedRef: UseRemoteDataSourceOptions<
			TDataItem,
			TFilterState,
			TConfiguration & HasLazyLoadPartRemoteDataSourceConfiguration,
			TGlobalContext
		>
	): RemoteDataSource<TDataItem, TConfiguration> => {
		const hookOptions: UseRemoteDataSourceOptions<
			TDataItem,
			TFilterState,
			TConfiguration & HasLazyLoadPartRemoteDataSourceConfiguration,
			TGlobalContext
			> = useValueEqualMemo(
				() => hookOptionsWithoutPreservedRef,
				[hookOptionsWithoutPreservedRef]
			);

		const { desiredAmountOfItems: _, ...optionsForListInvalidation } = hookOptions;
		const optionsVersion: number = useDeepComparison(optionsForListInvalidation);
		const [reload, reloadVersion] = useForceRerender();
		const [itemsState, setItemsState] = useState<LazyLoadItemsState<TDataItem>>({
			loadedItems: [],
			hasMoreOnServer: true,
			skipToken: { isSet: false }
		});

		const {
			loadedItems,
			hasMoreOnServer,
			skipToken
		} = itemsState;

		const [getLoadingState, setLoadingState] = useImmediateState<RemoteDataSourceState>("ready");
		const [hasLazyLoadError, setHasLazyLoadError] = useState<boolean>(false);

		const amountOfLoadedItems: number = loadedItems.length;
		const lastLoadedItem: TDataItem | undefined = loadedItems[loadedItems.length - 1];
		const stableSkipToken = useValueEqualMemo(
			(): OptionalModel<string | null> => skipToken,
			[skipToken]
		);

		useEffect(
			(): void => {
				hasImplicitDependencyOn(optionsVersion);
				hasImplicitDependencyOn(reloadVersion);

				setItemsState({
					loadedItems: [],
					hasMoreOnServer: true,
					skipToken: { isSet: false }
				});

				setHasLazyLoadError(false);
			},
			[
				optionsVersion,
				reloadVersion
			]
		);

		useEffect(
			() => {
				hasImplicitDependencyOn(reloadVersion);

				async function fetchItems(signal: AbortSignal): Promise<void> {
					const {
						desiredAmountOfItems,
						globalContext,
						sorting,
						onError
					} = hookOptions;

					const shouldLoadNewData: boolean = amountOfLoadedItems < desiredAmountOfItems;
					if (!shouldLoadNewData
							|| !hasMoreOnServer
							|| hasLazyLoadError
							|| (stableSkipToken.isSet && !stableSkipToken.value)
					) {
						return;
					}

					const amountOfItemsToLoad: number = desiredAmountOfItems - amountOfLoadedItems;

					const filteringOptions: FilteringPartRemoteDataSourceRequestOptions<TConfiguration, TFilterState> = getFilteringRequestPart<
						TDataItem,
						TFilterState,
						TConfiguration,
						TGlobalContext
					>(
						configuration,
						hookOptions
					);

					const searchOptions: SearchPartRemoteDataSourceRequestOptions<TConfiguration> = getSearchRequestPart<
						TDataItem,
						TFilterState,
						TConfiguration,
						TGlobalContext
					>(
						configuration,
						hookOptions
					);

					const paginationOptions = { } as PaginationPartRemoteDataSourceRequestOptions<TConfiguration>;
					let lazyLoadOptions: LazyLoadPartRemoteDataSourceRequestOptions<TDataItem, TConfiguration>;
					if (!configuration.lazyLoadCursor) {
						throw new Error("Lazy loaded cursor is not specified");
					}

					switch (configuration.lazyLoadCursor) {
						case LazyLoadCursorKind.AfterLastItem:
							const afterLastItemLazyLoadOptions: AfterLastItemLazyLoadPartRemoteDataSourceRequestOptions<TDataItem> = {
								after: lastLoadedItem,
								amount: amountOfItemsToLoad
							};
							lazyLoadOptions = afterLastItemLazyLoadOptions as LazyLoadPartRemoteDataSourceRequestOptions<TDataItem, TConfiguration>;
							break;
						case LazyLoadCursorKind.SkipTake:
							const skipTakeLazyLoadOptions: SkipTakeLazyLoadPartRemoteDataSourceRequestOptions = {
								skip: amountOfLoadedItems,
								amount: amountOfItemsToLoad
							};
							lazyLoadOptions = skipTakeLazyLoadOptions as LazyLoadPartRemoteDataSourceRequestOptions<TDataItem, TConfiguration>;
							break;
						case LazyLoadCursorKind.SkipToken:
							const skipTokenLazyLoadOptions: SkipTokenLazyLoadPartRemoteDataSourceRequestOptions = {
								skipToken: stableSkipToken.isSet
									? stableSkipToken.value
									: null,
								amount: amountOfItemsToLoad
							};
							lazyLoadOptions = skipTokenLazyLoadOptions as LazyLoadPartRemoteDataSourceRequestOptions<TDataItem, TConfiguration>;
							break;
						default:
							throw new UnreachableCaseError(configuration);
					}

					const globalContextOptions = {
						globalContext
					} as GlobalContextPartRemoteDataSourceRequestOptions<TGlobalContext>;

					const sortingOptions: SortingPartRemoteDataSourceRequestOptions<TDataItem> = {
						sorting
					};

					const cancellationOptions: CancellationTokenPartRemoteDataSourceRequestOptions = {
						signal
					};

					try {
						setLoadingState("loading");
						const response: RemoteDataSourceResponse<TDataItem, TConfiguration> = await performRequest({
							...filteringOptions,
							...searchOptions,
							...paginationOptions,
							...globalContextOptions,
							...lazyLoadOptions,
							...sortingOptions,
							...cancellationOptions
						});

						if (signal.aborted) {
							return;
						}

						if (!isLazyLoadResponse<TDataItem>(response) && !isSkipTokenPageResult(response)) {
							throw new Error("Expected lazy load response at this point.");
						}

						setItemsState(itemsState => {
							if (isLazyLoadResponse<TDataItem>(response)) {
								return {
									loadedItems: [
										...itemsState.loadedItems,
										...response.items
									],
									hasMoreOnServer: response.hasMore,
									skipToken: itemsState.skipToken
								};
							}

							return {
								loadedItems: [
									...itemsState.loadedItems,
									...response.items
								],
								hasMoreOnServer: response.skipToken !== null,
								skipToken: {
									isSet: true,
									value: response.skipToken
								}
							};
						});

						setLoadingState("ready");
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						if (onError) {
							onError(e);
						} else {
							console.error("Unable to perform lazy load request", e);
						}

						if (amountOfLoadedItems > 0) {
							setLoadingState("ready");
							setHasLazyLoadError(true);
						} else {
							setLoadingState("error");
						}
					}
				}

				const cancellation: AbortController = new AbortController();
				fetchItems(cancellation.signal);
				return () => cancellation.abort();
			},
			[
				hasMoreOnServer,
				hookOptions,
				lastLoadedItem,
				setLoadingState,
				amountOfLoadedItems,
				hasLazyLoadError,
				reloadVersion,
				stableSkipToken
			]
		);

		const baseResult: BaseRemoteDataSource<TDataItem> = {
			currentItems: loadedItems,
			getLoadingState,
			reload
		};

		const retryToLoadLastPage = useCallback(
			(): void => {
				setHasLazyLoadError(false);
			},
			[]
		);

		const lazyLoadResult = {
			hasMoreOnServer,
			hasLazyLoadError,
			retryToLoadLastPage
		} as LazyLoadResult<TConfiguration>;

		const paginationResult = { } as PaginationResult<TConfiguration>;

		return {
			...baseResult,
			...lazyLoadResult,
			...paginationResult
		};
	};
}

type StateAccessor<S> = () => S;
type StateModifier<S> = (newState: S) => void;

function useImmediateState<S>(): [StateAccessor<S | undefined>, StateModifier<S>];
function useImmediateState<S>(initialState: S): [StateAccessor<S>, StateModifier<S>];
function useImmediateState<S>(initialState?: S): [StateAccessor<S | undefined>, StateModifier<S>] {
	const stateRef: RefObject<S | undefined> = useRef<S>(initialState);
	const [rerender, stateVersion] = useForceRerender();

	const accessor: StateAccessor<S | undefined> = useCallback(
		() => {
			void stateVersion;
			return stateRef.current;
		},
		[stateVersion]
	);

	const modifier: StateModifier<S> = useCallback(
		(newState: S): void => {
			stateRef.current = newState;
			rerender();
		},
		[rerender]
	);

	return [accessor, modifier];
}

function getFilteringRequestPart<TDataItem, TFilterState, TConfiguration extends RemoteDataSourceConfiguration, TGlobalContext>(
	configuration: TConfiguration,
	options: UseRemoteDataSourceOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
): FilteringPartRemoteDataSourceRequestOptions<TConfiguration, TFilterState> {
	if (!doesSupportFiltering(configuration)) {
		return { } as FilteringPartRemoteDataSourceRequestOptions<TConfiguration, TFilterState>;
	}

	if (!areFilteringOptionsProvided<TConfiguration>(options)) {
		throw new Error("Filtering options are not provided for the filterable data source.");
	}

	const result: HasFilteringPartRemoteDataSourceRequestOptions<unknown> = {
		filterState: options.filtering
	};

	return result as FilteringPartRemoteDataSourceRequestOptions<TConfiguration, TFilterState>;
}

function getSearchRequestPart<TDataItem, TFilterState, TConfiguration extends RemoteDataSourceConfiguration, TGlobalContext>(
	configuration: TConfiguration,
	options: UseRemoteDataSourceOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
): SearchPartRemoteDataSourceRequestOptions<TConfiguration> {
	if (!doesSupportSearch(configuration)) {
		return { } as SearchPartRemoteDataSourceRequestOptions<TConfiguration>;
	}

	if (!areSearchOptionsProvided(options)) {
		throw new Error("Search options are not provided for the data source with search support.");
	}

	const result: HasSearchPartRemoteDataSourceRequestOptions = {
		searchTerm: options.searchTerm
	};

	return result as SearchPartRemoteDataSourceRequestOptions<TConfiguration>;
}

function getPaginationRequestPart<TDataItem, TFilterState, TConfiguration extends RemoteDataSourceConfiguration, TGlobalContext>(
	configuration: TConfiguration,
	options: UseRemoteDataSourceOptions<TDataItem, TFilterState, TConfiguration, TGlobalContext>
): PaginationPartRemoteDataSourceRequestOptions<TConfiguration> {
	if (!doesSupportPagination(configuration)) {
		return { } as PaginationPartRemoteDataSourceRequestOptions<TConfiguration>;
	}

	if (!arePaginationOptionsProvided(options)) {
		throw new Error("Pagination options are not provided for the pageable data source.");
	}

	const {
		currentPageNumber,
		amountPerPage = DEFAULT_AMOUNT_PER_PAGE
	} = options.pagination;

	const result: HasPaginationPartRemoteDataSourceRequestOptions = {
		pageNumber: currentPageNumber,
		amountPerPage: amountPerPage
	};

	return result as PaginationPartRemoteDataSourceRequestOptions<TConfiguration>;
}

function doesSupportFiltering(
	configuration: RemoteDataSourceConfiguration
): configuration is HasFilteringPartRemoteDataSourceConfiguration {
	return Boolean(configuration.hasFiltering);
}

function doesSupportSearch(
	configuration: RemoteDataSourceConfiguration
): configuration is HasSearchPartRemoteDataSourceConfiguration {
	return Boolean(configuration.hasSearch);
}

function doesSupportLazyLoad(
	configuration: RemoteDataSourceConfiguration
): configuration is (
	| AfterLastItemLazyLoadPartRemoteDataSourceConfiguration
	| SkipTakeLazyLoadPartRemoteDataSourceConfiguration
	| SkipTokenLazyLoadPartRemoteDataSourceConfiguration
) {
	return configuration.lazyLoadCursor === LazyLoadCursorKind.AfterLastItem
		|| configuration.lazyLoadCursor === LazyLoadCursorKind.SkipTake
		|| configuration.lazyLoadCursor === LazyLoadCursorKind.SkipToken;
}

function doesSupportPagination(
	configuration: RemoteDataSourceConfiguration
): configuration is HasPaginationPartRemoteDataSourceConfiguration {
	return Boolean(configuration.hasPagination);
}

function areFilteringOptionsProvided<TConfiguration>(
	options: HasFilteringPartUseRemoteDataSourceOptions<TConfiguration> | {}
): options is HasFilteringPartUseRemoteDataSourceOptions<TConfiguration> {
	return Boolean((options as HasFilteringPartUseRemoteDataSourceOptions<TConfiguration>).filtering);
}

function areSearchOptionsProvided(
	_options: HasSearchPartUseRemoteDataSourceOptions | {}
): _options is HasSearchPartUseRemoteDataSourceOptions {
	// searchTerm could be unset even if data source supports it
	return true;
}

function arePaginationOptionsProvided(
	options: HasPaginationPartUseRemoteDataSourceOptions | {}
): options is HasPaginationPartUseRemoteDataSourceOptions {
	return Boolean((options as HasPaginationPartUseRemoteDataSourceOptions).pagination);
}

function isLazyLoadResponse<TDataItem>(
	response:
		| LazyLoadPageResult<TDataItem>
		| SkipTokenPageResult<TDataItem>
		| PageResult<TDataItem>
		| readonly TDataItem[]
): response is LazyLoadPageResult<TDataItem> {
	return Boolean((response as LazyLoadPageResult<TDataItem>))
		&& typeof (response as LazyLoadPageResult<TDataItem>).hasMore === "boolean";
}

function isSkipTokenPageResult<TDataItem>(
	response:
		| LazyLoadPageResult<TDataItem>
		| SkipTokenPageResult<TDataItem>
		| PageResult<TDataItem>
		| readonly TDataItem[]
): response is SkipTokenPageResult<TDataItem> {
	return Boolean((response as SkipTokenPageResult<TDataItem>))
		&& (
			typeof (response as SkipTokenPageResult<TDataItem>).skipToken === "string"
				|| (response as SkipTokenPageResult<TDataItem>).skipToken === null
		);
}

function isPaginationResponse<TDataItem>(
	response:
		| LazyLoadPageResult<TDataItem>
		| SkipTokenPageResult<TDataItem>
		| PageResult<TDataItem>
		| readonly TDataItem[]
): response is PageResult<TDataItem> {
	return Boolean((response as PageResult<TDataItem>))
		&& typeof (response as PageResult<TDataItem>).totalCount === "number";
}

function isArrayResponse<TDataItem>(
	response:
	| LazyLoadPageResult<TDataItem>
	| SkipTokenPageResult<TDataItem>
	| PageResult<TDataItem>
	| readonly TDataItem[]
): response is readonly TDataItem[] {
	return Array.isArray(response);
}

type PaginationItemsState<TDataItem> = {
	currentItems: readonly TDataItem[];
	totalAmountOfItems: number;
};

type LazyLoadItemsState<TDataItem> = {
	loadedItems: readonly TDataItem[];
	hasMoreOnServer: boolean;
	skipToken: OptionalModel<string | null>;
};